import { useHotkeys } from "react-hotkeys-hook";
import { useGlobalModals } from "../components/shortcutContext";
import { useNavigation } from "../components/navigation";
import { useTranslation } from "react-i18next";
import {
  getIncomeData,
  getExpensesData,
  getCustomersData,
  getSuppliersData,
  getEmployeesData,
  getBuysData,
  getCashBookData,
  getSalesData,
  getCreditNotesData,
  getListsData,
} from "../store/actions/action";
import { useDispatch, useSelector } from "react-redux";
import { isMac } from "../components/helpershorcut";

const GlobalShortcuts = () => {
  const { openModal, closeAllModals } = useGlobalModals();
  const { navigate } = useNavigation();
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const buys = useSelector((state) => state?.buys.buys);
  const userRole = localStorage.getItem("role") || "";
  const isReseller = userRole === "reseller";
  const pathname = window.location.pathname;

  const isCustomerSide =
    pathname.includes("/online-store/") ||
    pathname.includes("/store/") ||
    pathname.startsWith("/online-store");

  const isAdminOnlineStore =
    pathname.endsWith("/online-store") ||
    pathname.includes("/online-store-orders") ||
    pathname.includes("/dashboard");

  const shouldDisableShortcuts = isCustomerSide && !isAdminOnlineStore;
  const isShortcutEnabled = !isReseller && !shouldDisableShortcuts;

  const purchaseNumber = 1001 + (buys?.length || 0);

  // Platform-aware modifier key (Command on Mac, Ctrl on others)
  const modKey = isMac() ? "command" : "ctrl";
  const altModKey = isMac() ? "command+alt" : "ctrl+shift";
  const getBusinessUuid = () => {
    const pathParts = window.location.pathname.includes("/dashboard")
      ? window.location.pathname.split("/dashboard")[0].split("/")
      : window.location.pathname.split("/");
    return pathParts.find((part) =>
      /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(
        part,
      ),
    );
  };

  // Common handler for all shortcuts
  const handleShortcut = async (e, options) => {
    console.log("LINE NO 45", e, options);
    e.preventDefault();
    closeAllModals();

    const businessUuid = getBusinessUuid();
    if (!businessUuid) {
      console.error("Business UUID not found in URL");
      return;
    }

    const { path, modalName, modalProps } = options;

    if (path && !window.location.pathname.includes(path)) {
      await navigate(`/${businessUuid}/dashboard${path}`);
    }

    if (modalName) {
      openModal(modalName, {
        ...modalProps,
        businessUuid,
      });
    }
  };

  // ============== Modal Shortcuts ==============
  // Income (Command/Ctrl + I)
  useHotkeys(
    `${modKey}+i`,
    (e) =>
      handleShortcut(e, {
        path: "/bills/income",
        modalName: "incomeForm",
        modalProps: {
          title: t("LANGUAGE.NEW_INCOME"),
          onSuccess: () => dispatch(getIncomeData(getBusinessUuid())),
        },
      }),
    {
      enableOnFormTags: ["INPUT", "TEXTAREA"],
      preventDefault: true,
      enableOnContentEditable: true,
      enabled: isShortcutEnabled,
    },
  );

  // Expense (Command/Ctrl + E)
  useHotkeys(
    `${modKey}+e`,
    (e) =>
      handleShortcut(e, {
        path: "/bills/dukanExpenses",
        modalName: "expenseForm",
        modalProps: {
          title: t("LANGUAGE.NEW_EXPENSE"),
          onSuccess: () => dispatch(getExpensesData(getBusinessUuid())),
        },
      }),
    {
      enableOnFormTags: ["INPUT", "TEXTAREA"],
      preventDefault: true,
      enableOnContentEditable: true,
      enabled: isShortcutEnabled,
    },
  );

  // Customer (Command/Ctrl + F)
  useHotkeys(
    `${modKey}+f`,
    (e) =>
      handleShortcut(e, {
        path: "/buddies/customers",
        modalName: "customerForm",
        modalProps: {
          title: t("LANGUAGE.NEW_CUSTOMER"),
          onSuccess: () => dispatch(getCustomersData(getBusinessUuid())),
        },
      }),
    {
      enableOnFormTags: ["INPUT", "TEXTAREA"],
      preventDefault: true,
      enableOnContentEditable: true,
      enabled: isShortcutEnabled,
    },
  );

  // Supplier (Command/Ctrl + V)
  useHotkeys(
    `${modKey}+x`,
    (e) =>
      handleShortcut(e, {
        path: "/buddies/suppliers",
        modalName: "supplierForm",
        modalProps: {
          title: t("LANGUAGE.ADD_SUPPLIER"),
          onSuccess: () => dispatch(getSuppliersData(getBusinessUuid())),
        },
      }),
    {
      enableOnFormTags: ["INPUT", "TEXTAREA"],
      preventDefault: true,
      enableOnContentEditable: true,
      enabled: isShortcutEnabled,
    },
  );

  // Employee (Command/Ctrl + P)
  useHotkeys(
    `${modKey}+p`,
    (e) =>
      handleShortcut(e, {
        path: "/buddies/employees",
        modalName: "employeeForm",
        modalProps: {
          title: t("LANGUAGE.ADD_EMPLOYEE"),
          onSuccess: () => dispatch(getEmployeesData(getBusinessUuid())),
        },
      }),
    {
      enableOnFormTags: ["INPUT", "TEXTAREA"],
      preventDefault: true,
      enableOnContentEditable: true,
      enabled: isShortcutEnabled,
    },
  );

  // Purchase (Command/Ctrl + B)
  useHotkeys(
    `${modKey}+b`,
    (e) =>
      handleShortcut(e, {
        path: "/bills/buy",
        modalName: "purchaseForm",
        modalProps: {
          title: `${t("LANGUAGE.PURCHASE_ORDER")} (${purchaseNumber})`,
          onSuccess: () => dispatch(getBuysData(getBusinessUuid())),
        },
      }),
    {
      enableOnFormTags: ["INPUT", "TEXTAREA"],
      preventDefault: true,
      enableOnContentEditable: true,
      enabled: isShortcutEnabled,
    },
  );

  // Cashbook (Command/Ctrl + K)
  useHotkeys(
    `${modKey}+k`,
    (e) =>
      handleShortcut(e, {
        path: "/cashBook",
        modalName: "cashbookForm",
        modalProps: {
          title: t("LANGUAGE.NEW_TRANSACTION"),
          onSuccess: () => dispatch(getCashBookData(getBusinessUuid())),
        },
      }),
    {
      enableOnFormTags: ["INPUT", "TEXTAREA"],
      preventDefault: true,
      enableOnContentEditable: true,
      enabled: isShortcutEnabled,
    },
  );

  // Sale (Command/Ctrl + S)
  useHotkeys(
    `${modKey}+s`,
    (e) =>
      handleShortcut(e, {
        path: "/bills/sale",
        modalName: "saleForm",
        modalProps: {
          title: t("LANGUAGE.NEW_SALE"),
          onSuccess: () => dispatch(getSalesData(getBusinessUuid())),
        },
      }),
    {
      enableOnFormTags: ["INPUT", "TEXTAREA"],
      preventDefault: true,
      enableOnContentEditable: true,
      enabled: isShortcutEnabled,
    },
  );

  useHotkeys(
    `${modKey}+4`,
    (e) =>
      handleShortcut(e, {
        path: "/tasks?isOpen=true",
        // modalName: "taskForm",
        // modalProps: {
        //   title: "task from shortcut",
        //   onSuccess: () => dispatch(getListsData(getBusinessUuid())),
        // },
      }),
    {
      enableOnFormTags: ["INPUT", "TEXTAREA"],
      preventDefault: true,
      enableOnContentEditable: true,
      enabled: isShortcutEnabled,
    },
  );

  // ============== Navigation Shortcuts ==============
  // Help Support (Command/Ctrl + H)
  useHotkeys(
    `${modKey}+h`,
    (e) =>
      handleShortcut(e, {
        path: "/help-support",
      }),
    {
      enableOnFormTags: ["INPUT", "TEXTAREA"],
      preventDefault: true,
      enableOnContentEditable: true,
      enabled: isShortcutEnabled,
    },
  );

  // Chat (Command/Ctrl + M)
  useHotkeys(
    `${modKey}+m`,
    (e) =>
      handleShortcut(e, {
        path: "/chat",
      }),
    {
      enableOnFormTags: ["INPUT", "TEXTAREA"],
      preventDefault: true,
      enableOnContentEditable: true,
      enabled: isShortcutEnabled,
    },
  );

  // Retail Rocket (Command/Ctrl + R)
  useHotkeys(
    `${modKey}+r`,
    (e) =>
      handleShortcut(e, {
        path: "/retailRocket",
      }),
    {
      enableOnFormTags: ["INPUT", "TEXTAREA"],
      preventDefault: true,
      enableOnContentEditable: true,
      enabled: isShortcutEnabled,
    },
  );

  // Tasks (Command/Ctrl + O)
  useHotkeys(
    `${modKey}+o`,
    (e) =>
      handleShortcut(e, {
        path: "/tasks",
      }),
    {
      enableOnFormTags: ["INPUT", "TEXTAREA"],
      preventDefault: true,
      enableOnContentEditable: true,
      enabled: isShortcutEnabled,
    },
  );

  // Reports (Command/Ctrl + D)
  useHotkeys(
    `${modKey}+d`,
    (e) =>
      handleShortcut(e, {
        path: "/reportPage",
      }),
    {
      enableOnFormTags: ["INPUT", "TEXTAREA"],
      preventDefault: true,
      enableOnContentEditable: true,
      enabled: isShortcutEnabled,
    },
  );

  // Inventory (Command/Ctrl + G)
  useHotkeys(
    `${modKey}+g`,
    (e) =>
      handleShortcut(e, {
        path: "/inventory",
      }),
    {
      enableOnFormTags: ["INPUT", "TEXTAREA"],
      preventDefault: true,
      enableOnContentEditable: true,
      enabled: isShortcutEnabled,
    },
  );
  useHotkeys(
    `${modKey}+u`,
    (e) =>
      handleShortcut(e, {
        path: "/bills/credit-debit",
      }),
    {
      enableOnFormTags: ["INPUT", "TEXTAREA"],
      preventDefault: true,
      enableOnContentEditable: true,
      enabled: isShortcutEnabled,
    },
  );
  useHotkeys(
    `${altModKey}+h`,
    (e) =>
      handleShortcut(e, {
        path: "/accounting-report-page",
      }),
    {
      enableOnFormTags: ["INPUT", "TEXTAREA"],
      preventDefault: true,
      enableOnContentEditable: true,
      enabled: isShortcutEnabled,
    },
  );
  useHotkeys(
    `${altModKey}+c`,
    (e) =>
      handleShortcut(e, {
        path: "/custom-report-page",
      }),
    {
      enableOnFormTags: ["INPUT", "TEXTAREA"],
      preventDefault: true,
      enableOnContentEditable: true,
      enabled: isShortcutEnabled,
    },
  );
  useHotkeys(
    `${altModKey}+k`,
    (e) =>
      handleShortcut(e, {
        path: "/sale-report-page",
      }),
    {
      enableOnFormTags: ["INPUT", "TEXTAREA"],
      preventDefault: true,
      enableOnContentEditable: true,
      enabled: isShortcutEnabled,
    },
  );
  useHotkeys(
    `${altModKey}+b`,
    (e) =>
      handleShortcut(e, {
        path: "/purchase-page",
      }),
    {
      enableOnFormTags: ["INPUT", "TEXTAREA"],
      preventDefault: true,
      enableOnContentEditable: true,
      enabled: isShortcutEnabled,
    },
  );
  useHotkeys(
    `${altModKey}+i`,
    (e) =>
      handleShortcut(e, {
        path: "/inventory-report-page",
      }),
    {
      enableOnFormTags: ["INPUT", "TEXTAREA"],
      preventDefault: true,
      enableOnContentEditable: true,
      enabled: isShortcutEnabled,
    },
  );
  useHotkeys(
    `${altModKey}+p`,
    (e) =>
      handleShortcut(e, {
        path: "/customer-report-page",
      }),
    {
      enableOnFormTags: ["INPUT", "TEXTAREA"],
      preventDefault: true,
      enableOnContentEditable: true,
      enabled: isShortcutEnabled,
    },
  );
  useHotkeys(
    `${altModKey}+d`,
    (e) =>
      handleShortcut(e, {
        path: "/return-report-page",
      }),
    {
      enableOnFormTags: ["INPUT", "TEXTAREA"],
      preventDefault: true,
      enableOnContentEditable: true,
      enabled: isShortcutEnabled,
    },
  );
  useHotkeys(
    `${altModKey}+e`,
    (e) =>
      handleShortcut(e, {
        path: "/expense-page",
      }),
    {
      enableOnFormTags: ["INPUT", "TEXTAREA"],
      preventDefault: true,
      enableOnContentEditable: true,
      enabled: isShortcutEnabled,
    },
  );
  useHotkeys(
    `${altModKey}+g`,
    (e) =>
      handleShortcut(e, {
        path: "/gst-report-page",
      }),
    {
      enableOnFormTags: ["INPUT", "TEXTAREA"],
      preventDefault: true,
      enableOnContentEditable: true,
      enabled: isShortcutEnabled,
    },
  );
  return null;
};

export default GlobalShortcuts;
